interface PaperPreviewModalProps {
  paper: {
    id: string;
    board: string;
    year: string;
    paper: string;
    title: string;
    type: string;
    pages: number;
    size: string;
    level?: string;
  } | null;
  onClose: () => void;
  typeColors: Record<string, string>;
  typeIcons: Record<string, string>;
  boardBadgeColor: string;
}

const mockSections: Record<string, { heading: string; lines: number[] }[]> = {
  'Question Paper': [
    { heading: 'Section A — Short Answer Questions', lines: [80, 100, 65, 90, 75, 100, 55] },
    { heading: 'Section B — Extended Response', lines: [100, 85, 70, 95, 60, 100, 80, 45] },
    { heading: 'Section C — Programming Task', lines: [100, 90, 75, 100, 65, 80] },
  ],
  'Mark Scheme': [
    { heading: 'Section A — Mark Allocations', lines: [70, 100, 55, 85, 60, 100, 45] },
    { heading: 'Section B — Acceptable Answers', lines: [100, 80, 65, 90, 75, 100, 55, 70] },
    { heading: 'Examiner Notes', lines: [90, 60, 100, 50, 75] },
  ],
  'Examiner Report': [
    { heading: 'Overall Performance Summary', lines: [100, 85, 70, 95, 60] },
    { heading: 'Question-by-Question Analysis', lines: [80, 100, 65, 90, 75, 100, 55, 85] },
    { heading: 'Common Errors & Misconceptions', lines: [100, 70, 85, 55, 90, 65] },
  ],
};

export default function PaperPreviewModal({ paper, onClose, typeColors, typeIcons, boardBadgeColor }: PaperPreviewModalProps) {
  if (!paper) return null;

  const sections = mockSections[paper.type] ?? mockSections['Question Paper'];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
      <div
        className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 flex items-center justify-center bg-emerald-50 rounded-xl flex-shrink-0">
              <i className={`${typeIcons[paper.type] ?? 'ri-file-text-line'} text-emerald-600 text-lg`}></i>
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${boardBadgeColor}`}>{paper.board}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeColors[paper.type] ?? 'bg-slate-100 text-slate-700'}`}>{paper.type}</span>
                {paper.level && (
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-slate-100 text-slate-600">{paper.level}</span>
                )}
              </div>
              <h3 className="text-base font-bold text-slate-900 leading-snug truncate">
                {paper.paper} — {paper.title}
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">{paper.year} · {paper.pages} pages · {paper.size}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-slate-500 text-lg"></i>
          </button>
        </div>

        {/* Document Preview */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Paper header simulation */}
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 mb-4">
            <div className="flex items-center gap-2 mb-4">
              <i className="ri-file-text-line text-slate-400 text-sm"></i>
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Document Preview</span>
              <span className="ml-auto text-xs text-slate-400 bg-white border border-slate-200 px-2 py-0.5 rounded-full">Page 1 of {paper.pages}</span>
            </div>

            {/* Simulated exam header */}
            <div className="bg-white rounded-lg border border-slate-200 p-4 mb-3">
              <div className="flex items-center justify-between mb-3">
                <div className="h-5 bg-slate-800 rounded w-24"></div>
                <div className="h-4 bg-slate-200 rounded w-16"></div>
              </div>
              <div className="h-3 bg-slate-200 rounded w-3/4 mb-1.5"></div>
              <div className="h-3 bg-slate-200 rounded w-1/2 mb-3"></div>
              <div className="border-t border-slate-200 pt-3">
                <div className="h-2.5 bg-slate-100 rounded w-full mb-1.5"></div>
                <div className="h-2.5 bg-slate-100 rounded w-5/6 mb-1.5"></div>
                <div className="h-2.5 bg-slate-100 rounded w-4/5"></div>
              </div>
            </div>

            {/* Simulated sections */}
            {sections.map((section, si) => (
              <div key={si} className="bg-white rounded-lg border border-slate-200 p-4 mb-3 last:mb-0">
                <div className="h-3.5 bg-slate-700 rounded w-2/3 mb-3"></div>
                <div className="space-y-1.5">
                  {section.lines.map((w, li) => (
                    <div
                      key={li}
                      className="h-2 bg-slate-100 rounded"
                      style={{ width: `${w}%` }}
                    ></div>
                  ))}
                </div>
              </div>
            ))}

            {/* Blurred / locked overlay for remaining pages */}
            <div className="mt-3 flex items-center justify-center py-4 bg-white/90 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 text-slate-400">
                <i className="ri-eye-line text-sm"></i>
                <p className="text-xs font-medium">
                  Showing preview — {paper.pages} pages total
                </p>
              </div>
            </div>
          </div>

          {/* Meta info row */}
          <div className="flex flex-wrap gap-4 text-xs text-slate-500 mb-2">
            <span className="flex items-center gap-1.5"><i className="ri-pages-line text-emerald-600"></i>{paper.pages} pages</span>
            <span className="flex items-center gap-1.5"><i className="ri-file-line text-emerald-600"></i>{paper.size}</span>
            <span className="flex items-center gap-1.5"><i className="ri-calendar-line text-emerald-600"></i>{paper.year}</span>
            <span className="flex items-center gap-1.5"><i className="ri-bookmark-line text-emerald-600"></i>{paper.paper}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 pb-6 pt-3 border-t border-slate-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Close
          </button>
          <button className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-download-line text-sm"></i>
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
}
